import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, parseISO } from "date-fns";
import { Send, MessageSquare, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";

interface ClientMessagesProps {
  clientId: string;
  clinicianId: string;
  clinicianClientId: string;
  practitionerType: string;
}

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  subject: string | null;
  content: string;
  read_at: string | null;
  created_at: string;
}

export function ClientMessages({ clientId, clinicianId, clinicianClientId, practitionerType }: ClientMessagesProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const practitionerLabel = practitionerType === 'coach' ? 'coach' : 'therapist';

  useEffect(() => {
    loadMessages();
    
    // Subscribe to new messages
    const channel = supabase
      .channel('client-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'clinician_messages',
          filter: `recipient_id=eq.${clientId}`
        },
        (payload) => {
          setMessages(prev => [...prev, payload.new as Message]);
          scrollToBottom();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId, clinicianId]);

  const loadMessages = async () => {
    const { data, error } = await supabase
      .from('clinician_messages')
      .select('*')
      .eq('clinician_client_id', clinicianClientId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      setLoading(false);
      return;
    }

    setMessages(data || []);
    setLoading(false);
    
    // Mark received messages as read
    const unreadIds = (data || [])
      .filter(m => m.recipient_id === clientId && !m.read_at)
      .map(m => m.id);

    if (unreadIds.length > 0) {
      await supabase
        .from('clinician_messages')
        .update({ read_at: new Date().toISOString() })
        .in('id', unreadIds);
    }

    setTimeout(scrollToBottom, 100);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    const { error } = await supabase
      .from('clinician_messages')
      .insert({
        sender_id: clientId,
        recipient_id: clinicianId,
        clinician_client_id: clinicianClientId,
        message_type: 'general',
        content: newMessage.trim()
      });

    if (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setSending(false);
      return;
    }

    // Add to local state immediately
    const newMsg: Message = {
      id: crypto.randomUUID(),
      sender_id: clientId,
      recipient_id: clinicianId,
      subject: null,
      content: newMessage.trim(),
      read_at: null,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMsg]);
    setNewMessage("");
    setSending(false);
    scrollToBottom();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card className="h-[500px] sm:h-[600px] flex flex-col bg-card/50 border-border/30">
      <CardHeader className="border-b border-border/30 p-3 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-foreground text-base sm:text-lg">
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          Messages
        </CardTitle>
        <CardDescription className="text-muted-foreground text-xs sm:text-sm">
          Message your {practitionerLabel}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea ref={scrollRef} className="flex-1 p-3 sm:p-4">
          <div className="space-y-3 sm:space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-muted-foreground">
                <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-sm">No messages yet</p>
                <p className="text-xs sm:text-sm">Send a message to start</p>
              </div>
            ) : (
              messages.map((message) => {
                const isFromMe = message.sender_id === clientId;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] sm:max-w-[80%] rounded-lg px-3 sm:px-4 py-2 ${
                        isFromMe
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
                      <div className={`flex items-center gap-1 text-[10px] sm:text-xs mt-1 ${
                        isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        <span>{format(parseISO(message.created_at), 'HH:mm')}</span>
                        {isFromMe && (
                          message.read_at ? (
                            <CheckCheck className="h-3 w-3" />
                          ) : (
                            <Check className="h-3 w-3" />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
        
        <div className="p-3 sm:p-4 border-t bg-background">
          <div className="flex gap-2">
            <Textarea
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[50px] sm:min-h-[60px] resize-none text-sm"
              disabled={sending}
            />
            <Button 
              onClick={handleSend} 
              disabled={!newMessage.trim() || sending}
              size="icon"
              className="self-end h-9 w-9 shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-2">
            Enter to send, Shift+Enter for new line
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
